import {
  coreExtensionData,
  createFrontendModule,
  createFrontendPlugin,
  ExtensionBoundary,
  PageBlueprint,
  PluginHeaderActionBlueprint,
} from '@backstage/frontend-plugin-api';
import { RiAddCircleLine, RiFileCodeLine } from '@remixicon/react';
import { filterPredicateToFilterFunction } from '@backstage/filter-predicates';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import { Route, Routes } from 'react-router-dom';
import {
  actionsTemplateRouteRef,
  catalogYamlGeneratorRouteRef,
  editTemplateRouteRef,
  rootRouteRef,
  scaffolderListTaskRouteRef,
  selectedTemplateRouteRef,
  templatesRouteRef,
  templatingExtensionsRouteRef,
  viewTechDocRouteRef,
} from './routes';
import { createHubTranslationRef } from './translation';

export const createHubPage = PageBlueprint.make({
  params: {
    path: '/create-hub',
    title: 'Create New Entity',
    icon: <RiAddCircleLine />,
    // LINK THE ROUTE HERE:
    routeRef: rootRouteRef,
    loader: () =>
      import('./components/CreateBase/CreateBase').then(m => (
        <m.CreateBasePage />
      )),
  },
});

export const catalogYamlGeneratorPage = PageBlueprint.make({
  name: 'catalog-yaml-generator',
  params: {
    path: '/create-catalog',
    title: 'Create Your Catalog Definition',
    icon: <RiFileCodeLine />,
    routeRef: catalogYamlGeneratorRouteRef,
    loader: () =>
      import('./yaml-generator/components/CatalogYamlGenerator').then(m => (
        <m.CatalogYamlGenerator />
      )),
  },
});

/**
 * Import YAML and Register Entity in the header of the create hub pages; they
 * hide themselves on the YAML generator and the management choice.
 */
export const createHubHeaderActions = PluginHeaderActionBlueprint.make({
  name: 'catalog-actions',
  params: {
    loader: () =>
      import('./components/HeaderAction/CreateHubHeaderActions').then(m => (
        <m.CreateHubHeaderActions />
      )),
  },
});

/** Publish in the header of the YAML generator, while it is open. */
export const yamlGeneratorPublishAction = PluginHeaderActionBlueprint.make({
  name: 'publish',
  params: {
    loader: () =>
      import('./components/HeaderAction/CreateHubHeaderActions').then(m => (
        <m.YamlGeneratorPublishAction />
      )),
  },
});

export const createHubPlugin = createFrontendPlugin({
  // The plugin ID is now provided as `pluginId` instead of `id`
  pluginId: 'create-hub',
  // bind all the extensions to the plugin
  extensions: [
    createHubPage,
    catalogYamlGeneratorPage,
    createHubHeaderActions,
    yamlGeneratorPublishAction,
  ],
  // Adding the routes mapping here ensures navigation works
  routes: {
    root: rootRouteRef,
    catalogYamlGenerator: catalogYamlGeneratorRouteRef,
  },
  externalRoutes: {
    viewTechDoc: viewTechDocRouteRef,
    selectedTemplate: selectedTemplateRouteRef,
    editTemplate: editTemplateRouteRef,
    actionsTemplate: actionsTemplateRouteRef,
    listTasksTemplate: scaffolderListTaskRouteRef,
    templatingExtensions: templatingExtensionsRouteRef,
  },
});

/**
 * Replaces the template list of the scaffolder (`sub-page:scaffolder/templates`
 * on `/create/templates`) with the create-hub list. The template filter and the
 * groups stay the config of the sub-page (predicates of
 * `@backstage/filter-predicates`). Every other path below the sub-page, the
 * template wizard on `:namespace/:templateName`, is still served by the
 * original sub-page with its form fields, layouts and form theme.
 */
const templatesSubPage = scaffolderPlugin
  .getExtension('sub-page:scaffolder/templates')
  .override({
    factory(originalFactory, { config, node }) {
      const subPage = originalFactory();

      const groups = config.groups?.map(group => ({
        title: group.title,
        filter: filterPredicateToFilterFunction<TemplateEntityV1beta3>(
          group.filter,
        ),
      }));
      const templateFilter =
        config.templateFilter === undefined
          ? undefined
          : filterPredicateToFilterFunction<TemplateEntityV1beta3>(
              config.templateFilter,
            );

      const templateList = ExtensionBoundary.lazy(node, () =>
        import('./components/CreateBase/Templatespage').then(m => (
          <m.TemplatesPage groups={groups} templateFilter={templateFilter} />
        )),
      );

      return [
        ...subPage,
        coreExtensionData.routeRef(templatesRouteRef),
        coreExtensionData.reactElement(
          <Routes>
            <Route index element={templateList} />
            <Route
              path="*"
              element={subPage.get(coreExtensionData.reactElement)}
            />
          </Routes>,
        ),
      ];
    },
  });

/**
 * Import YAML and Register Entity in the header of the scaffolder page, shown
 * on the template list only.
 */
export const templatesHeaderActions = PluginHeaderActionBlueprint.make({
  name: 'catalog-actions',
  params: {
    loader: () =>
      import('./components/HeaderAction/CreateHubHeaderActions').then(m => (
        <m.TemplatesHeaderActions />
      )),
  },
});

/**
 * Module of the scaffolder plugin: install it next to
 * `@backstage/plugin-scaffolder/alpha` and `createHubPlugin`.
 */
export const scaffolderTemplatesModule = createFrontendModule({
  pluginId: 'scaffolder',
  extensions: [templatesSubPage, templatesHeaderActions],
});

export { createHubTranslationRef };
